"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
/**
 * A 'library' of common aggregators.
 *
 * Pass one of these functions to a `Signal[n]` constructor to add the described behaviour to that signal.
 */
class Aggregators {
    /**
     * Basic "pass-through" aggregator. Will pass an array of values back to the signal emitter, one per callback.
     *
     * Note that this means the size of the returned array depends on the number of listeners attached.
     */
    static ARRAY(items) {
        return items;
    }
    /**
     * If a signal allows listeners to return a promise, this aggregator will await all the received promises and then
     * return an array of the resolved values.
     *
     * This aggregator is intended for use with non-void promises, where the value returned by the promise is
     * meaningful. If the return value of the promise is not significant, consider {@link AwaitVoid}.
     */
    static AWAIT(items) {
        return Promise.all(items);
    }
    /**
     * An aggregator for use with Promise<void> signals. Will await all promises that are returned by callback
     * functions, but discard the (void) return values.
     */
    static async AWAIT_VOID(items) {
        await Promise.all(items);
    }
}
exports.Aggregators = Aggregators;
/**
 * Signal implementation, a messaging paradigm similar to standard JavaScript events, only more object-based.
 *
 * Create a signal for each event type, specifying the types of any callback parameters. Listeners (known as "slots")
 * can then be added to (and removed from) the signal.
 *
 * Unlike events, callbacks are also able to return a value. To make use of these return values, pass an aggregator
 * function when defining the signal. The static functions within {@link Aggregators} are a set of aggregators that
 * cover typical use-cases.
 */
class Signal {
    constructor(aggregator) {
        this._slots = [];
        this.aggregator = aggregator || null;
    }
    /**
     * Provides read-only access to the current set of slots.
     *
     * This is an array of all callbacks that are currently attached to this signal.
     */
    get slots() {
        return this._slots;
    }
    add(callback, context) {
        const slot = { callback, context, remove: () => this.removeSlot(slot) };
        this._slots.push(slot);
        return slot;
    }
    remove(callback, context) {
        const index = this._slots.findIndex((ctx) => ctx.callback === callback && ctx.context === context);
        if (index >= 0) {
            this._slots.splice(index, 1);
            return true;
        }
        else {
            return false;
        }
    }
    has(callback, context) {
        return this._slots.findIndex((ctx) => ctx.callback === callback && ctx.context === context) >= 0;
    }
    emit(...args) {
        const slots = this._slots.slice(); // Clone array, in case a callback modifies this signal
        if (!this.aggregator) {
            slots.forEach(slot => slot.callback.apply(slot.context, args));
            // If not using an aggregator, return type should be void.
            // No way to enforce this through TypeScript without creating a whole new set of signal classes, so relying on convention here.
            return undefined;
        }
        else {
            return this.aggregator(slots.map(slot => slot.callback.apply(slot.context, args)));
        }
    }
    removeSlot(slot) {
        const index = this._slots.indexOf(slot);
        if (index >= 0) {
            this._slots.splice(index, 1);
        }
    }
}
exports.Signal = Signal;
